/**
 * Funnel analytics for lead forms.
 *
 * One consistently shaped event family so drop-off can be measured across
 * every form without reverse-engineering per-form event names:
 *
 *   form_start              — first interaction with any field
 *   form_validation_error   — client-side validation blocked the submit
 *   form_field_error        — one per invalid field (field-level drop-off)
 *   form_submit             — passed validation, request in flight
 *   form_submit_success     — endpoint accepted the lead
 *   form_submit_error       — endpoint/network failure
 *
 * Each event carries `form_id` and `form_name` so GA4 can build a funnel:
 * form_start -> form_submit -> form_submit_success.
 */
import type React from "react";
import { trackEvent } from "@/lib/tracking";

type Props = Record<string, string | number | boolean | undefined>;

export type FormIdentity = { formId: string; formName: string };

const started = new Set<string>();

function base({ formId, formName }: FormIdentity, extra: Props = {}): Props {
  return { form_id: formId, form_name: formName, ...extra };
}

/** Fires once per form per page load, on first field interaction. */
export function trackFormStart(form: FormIdentity, extra: Props = {}) {
  const key = `${form.formName}:${form.formId}`;
  if (started.has(key)) return;
  started.add(key);
  trackEvent("form_start", base(form, extra));
}

/** Reset (tests, or after a form is re-armed for another submission). */
export function resetFormStart(form?: FormIdentity) {
  if (!form) return started.clear();
  started.delete(`${form.formName}:${form.formId}`);
}

/**
 * Validation failure. `errors` maps field name -> message (undefined = valid).
 * Emits one summary event plus one event per invalid field.
 */
export function trackFormValidationError(
  form: FormIdentity,
  errors: Record<string, string | undefined>,
  extra: Props = {},
) {
  const invalid = Object.entries(errors)
    .filter(([, message]) => Boolean(message))
    .map(([field, message]) => ({ field, message: message as string }));
  if (invalid.length === 0) return;

  trackEvent(
    "form_validation_error",
    base(form, {
      error_count: invalid.length,
      fields: invalid.map((i) => i.field).join(","),
      first_error_field: invalid[0].field,
      ...extra,
    }),
  );

  for (const { field, message } of invalid) {
    trackEvent("form_field_error", base(form, { field, message, ...extra }));
  }
}

/** Passed validation — request about to go out. */
export function trackFormSubmit(form: FormIdentity, extra: Props = {}) {
  trackEvent("form_submit", base(form, extra));
}

/** Endpoint accepted the submission. */
export function trackFormSuccess(form: FormIdentity, extra: Props = {}) {
  trackEvent("form_submit_success", base(form, extra));
}

/** Endpoint or network failure. */
export function trackFormError(form: FormIdentity, message: string, extra: Props = {}) {
  trackEvent("form_submit_error", base(form, { message, ...extra }));
}

/* ------------------------------------------------------------------ */
/* Field-level interaction tracking                                    */
/* ------------------------------------------------------------------ */
/**
 * Delegated focus/blur/change tracking for every control inside a form.
 *
 *   form_field_focus     — first time a field receives focus (once per field)
 *   form_field_change    — first edit of a field (once per field)
 *   form_field_complete  — on blur, with `filled` + `dwell_ms`
 *
 * Spread the returned handlers on the <form> element:
 *   <form {...fieldTracker(formIdentity, { source })}>
 * Values are never sent — only field names, fill state, and timings.
 */
type FieldEl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

function fieldName(target: EventTarget | null): string | null {
  const el = target as FieldEl | null;
  if (!el || !("tagName" in el)) return null;
  const tag = (el as HTMLElement).tagName;
  if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return null;
  if ((el as HTMLInputElement).type === "hidden") return null;
  const name = el.name || el.id;
  if (!name) return null;
  // Honeypots are decoys — never report them as real interactions.
  if (/honeypot|company_website|hp_/i.test(name)) return null;
  return name;
}

export function fieldTracker(form: FormIdentity, extra: Props = {}) {
  const focused = new Set<string>();
  const changed = new Set<string>();
  const focusedAt = new Map<string, number>();

  return {
    onFocusCapture: (e: React.SyntheticEvent) => {
      const field = fieldName(e.target);
      if (!field) return;
      focusedAt.set(field, Date.now());
      if (focused.has(field)) return;
      focused.add(field);
      trackFormStart(form, { ...extra, first_field: field });
      trackEvent("form_field_focus", base(form, { field, field_index: focused.size, ...extra }));
    },
    onChangeCapture: (e: React.SyntheticEvent) => {
      const field = fieldName(e.target);
      if (!field || changed.has(field)) return;
      changed.add(field);
      trackFormStart(form, { ...extra, first_field: field });
      trackEvent("form_field_change", base(form, { field, ...extra }));
    },
    onBlurCapture: (e: React.SyntheticEvent) => {
      const field = fieldName(e.target);
      if (!field) return;
      const startedAt = focusedAt.get(field);
      focusedAt.delete(field);
      const value = (e.target as FieldEl).value ?? "";
      trackEvent(
        "form_field_complete",
        base(form, {
          field,
          filled: value.trim().length > 0,
          changed: changed.has(field),
          dwell_ms: startedAt ? Date.now() - startedAt : 0,
          ...extra,
        }),
      );
    },
  };
}
